import { createSlice } from '@reduxjs/toolkit';
import api from 'src/api/index';
import * as modulesAPI from 'src/api/modules';
import * as lecturesAPI from 'src/api/lectures';
import i18n from 'src/i18n';
import { showErrorToast } from './toast';
import { updateLecture } from './lectures';
import Uploader from 'src/tools/Uploader';
import ContentCreater from 'src/tools/ContentCreater';
import { reloadPublication } from './publications';

function generateRandomId () {
	return Date.now() + Math.floor(Math.random() * 1000000);
};

const initialState = {
	list: [],
	draft: null,
	draftModules: [],
	loading: false
};

const marathonsSlice = createSlice({
	name: 'marathons',
	initialState,
	reducers: {
		setMarathonsList: (state, action) => {
			state.list = action.payload;
		},
		setMarathonLoading: (state, action) => {
			state.loading = action.payload;
		},
		setMarathonDraft: (state, action) => {
			// Создаём копию без questionAnswers вместо delete (для immutable объектов)
			const { questionAnswers, ...payloadWithoutQA } = action.payload;
			if (state.draft) {
				state.draft = { ...state.draft, ...payloadWithoutQA };
			} else {
				state.draft = payloadWithoutQA;
			}
		},
		removeMarathonDraft: (state) => {
			state.draft = null;
			state.draftModules = [];
		},
		setDraftModules: (state, action) => {
			state.draftModules = action.payload;
		},
		addModuleDraft: (state, action) => {

			const id = generateRandomId();
			const orderNumber = state.draftModules.length;

			state.draftModules.push({ 
				...action.payload, 
				id, 
				orderNumber 
			});

		},
		updateModuleDraft: (state, action) => {

			const index = state.draftModules.findIndex(item => item.id === action.payload.id);

			if (index !== -1) {
				state.draftModules.splice(index, 1, action.payload);
			}

		},

		addDraftLectureOnModule: (state, action) => {

			const currentModule = state.draftModules.find(item => item.id === action.payload.module.id);
			if (currentModule) {
				currentModule.lectures.push(action.payload.lecture);
			}

		},
 		addMarathonsItem: (state, action) => {
			state.list.push(action.payload);
		},
		removeMarathonsItem: (state, action) => {
			state.list = state.list.filter((item) => action.payload !== item.id);
		},
		updateMarathonItem: (state, action) => {
			const index = state.list.findIndex(item => item.id === action.payload.id);
			if (index !== -1) {
				state.list.splice(index, 1, action.payload);
			}
		}
	},
});

export const { setMarathonsList, addMarathonsItem, removeMarathonsItem, updateMarathonItem,
	setMarathonDraft, setDraftModules, addDraftLectureOnModule, addModuleDraft,
	updateModuleDraft, setMarathonLoading, removeMarathonDraft } = marathonsSlice.actions;

export const fetchMarathonList = () => async (dispatch) => {
	try {

		const res = await api.marathonsList();
		dispatch(setMarathonsList(res.list));

	} catch (error) {

		throw error;
		
	}
};

export const getMarathonById = (id) => ( dispatch, getState ) => {

	return new Promise(( resolve, reject ) => {

		const state = getState();

		const list = state.marathons.list;

		let item = list.find(item => item.id === id);

		if (item) {
			// Обновляем draft и draftModules даже если курс уже в кеше
			dispatch(setMarathonDraft(item));
			dispatch(setDraftModules(item.modules || []));
			resolve(item);

		} else {

			api.marathonById(id)
				.then(res => {
					dispatch(addMarathonsItem(res));
					// Также обновляем draft и draftModules для корректной работы редактирования
					dispatch(setMarathonDraft(res));
					dispatch(setDraftModules(res.modules || []));
					resolve(res);
				})
				.catch(() => {

					dispatch(showErrorToast({
						description: i18n.t('toasts.courseNotFound', { id })
					}));

					reject();

				});
		}

	});

};

export const saveMarathonFromDraft = ( marathon ) => async ( dispatch, getState ) => {

	const queue = [];

	const state = getState();
	// draftMarathon обязательно должен быть копией курса, из аргументов
	const draftModules = state.marathons.draftModules;
	const draftMarathon = state.marathons.draft;
	
	// Получаем актуальный марафон с сервера для корректного сравнения
	let originalMarathon;
	try {
		originalMarathon = await api.marathonById(marathon.id);
	} catch (error) {
		// Если марафон не найден на сервере (новый марафон), используем переданный
		originalMarathon = marathon;
	}

	const sendLectureToModule = async (module, lectureId) => {
		// Получаем актуальный модуль с сервера чтобы не потерять уже добавленные лекции
		const currentMarathon = await api.marathonById(marathon.id);
		const currentModule = currentMarathon.modules.find(m => m.id === module.id);
		
		const module_copy = JSON.parse( JSON.stringify( module ) );
		// Берем актуальные лекции с сервера (не из локального состояния)
		module_copy.lectures = currentModule ? currentModule.lectures.map(l => l.id) : [];
		
		module_copy.lectures.push(lectureId);

		await dispatch(editMarathonModule({
			marathonId: marathon.id,
			module: module_copy
		}));

	};

	for (const module of draftModules) {

		async function updateOrCreateModule() {

			const findModule = originalMarathon.modules.find(item => item.id === module.id);

			let moduleId = module.id;

			if (module.isDraft) {
				// Создаем модуль если ранее такого модуля не было
				moduleId = await dispatch(createMarathonModule( { 
					
					marathonId: marathon.id,
					module: {
						name: module.name,
						lectures: []
					}

				}));
				
				// Если у модуля есть локальный Quiz, создаём его на сервере
				if (module.quiz && module.quiz.isDraft) {
					console.log('📝 Creating quiz for new module:', moduleId, module.quiz);
					console.log('📝 Quiz questions:', module.quiz.questions);
					
					const QuizManager = (await import('src/tools/QuizManger')).default;
					const quizManager = new QuizManager();
					
					const quiz = {
						name: module.quiz.name,
						timeEstimate: module.quiz.timeEstimate,
						quizableType: 'marathon_modules',
						quizableId: moduleId,
						isActive: true
					};
					
					// Преобразуем questions из массива в объект (как в QuizContext)
					const questionsArray = module.quiz.questions || [];
					
					// Если нет вопросов - создаём пустой quiz
					if (questionsArray.length === 0) {
						console.log('📝 Creating empty quiz (no questions)');
						await quizManager.createQuiz(quiz);
						console.log('✅ Empty quiz created for module:', moduleId);
					} else {
						// Если есть вопросы - создаём quiz с вопросами
						const questions = {};
						const options = {};
						
						questionsArray.forEach((q, index) => {
							const questionKey = q.tempId || q.id || `question_${index}`;
							
							console.log('🔍 Processing question:', questionKey, q);
							
							// Создаём копию вопроса без options (они передаются отдельно)
							const { options: questionOptions, ...questionWithoutOptions } = q;
							
							console.log('📋 Question options:', questionOptions);
							
							questions[questionKey] = {
								...questionWithoutOptions,
								tempId: questionKey
							};
							
							// Options хранятся отдельно по ключу вопроса
							if (questionOptions && questionOptions.length > 0) {
								options[questionKey] = questionOptions;
								console.log('✅ Added options for', questionKey, ':', questionOptions);
							} else {
								console.log('❌ No options for', questionKey);
							}
						});
						
						console.log('📋 Prepared questions:', questions);
						console.log('📋 Prepared options:', options);
						
						await quizManager.createQuizAllData(quiz, questions, options);
						console.log('✅ Quiz created for module:', moduleId);
					}
				}
			} else {
			// Сравниваем порядок лекций и другие поля модуля
				const newLectureIds = module.lectures.filter(item => !item.isDraft).map(item => item.id);
				const oldLectureIds = findModule.lectures.filter(item => !item.isDraft).map(item => item.id);

				// Удаляем лекции которые были заменены (есть в old, но нет в new)
				const removedLectureIds = oldLectureIds.filter(id => !newLectureIds.includes(id));
				for (const removedId of removedLectureIds) {
					try {
						await lecturesAPI.deleteLecture(removedId);
					} catch (e) {
						console.error('Failed to delete replaced lecture:', removedId, e);
					}
				}
			
				const lecturesChanged = JSON.stringify(newLectureIds) !== JSON.stringify(oldLectureIds);
				const moduleChanged = module.name !== findModule.name || module.orderNumber !== findModule.orderNumber;
			
				if (lecturesChanged || moduleChanged) {
				// Тут проверка на соответсвие с исходником
					console.log('🔄 Module changed, updating:', module.id);
					console.log('📝 Lectures changed:', lecturesChanged, 'New:', newLectureIds, 'Old:', oldLectureIds);
					console.log('📝 Module changed:', moduleChanged);

					await dispatch(editMarathonModule({

						marathonId: marathon.id,
						module: {
							id: module.id,
							name: module.name,
							lectures: newLectureIds,
							orderNumber: module.orderNumber
						}

					}));
				}
			
				// Проверяем наличие новых вопросов в quiz существующего модуля
				if (module.quiz && module.quiz.questions && module.quiz.questions.length > 0) {
					const isDraftQuiz = Boolean(module.quiz.isDraft) || String(module.quiz.id).startsWith('draft_quiz_');
					const draftQuestions = module.quiz.questions.filter(q => q.isDraft);

					if (isDraftQuiz || draftQuestions.length > 0) {
						const QuizManager = (await import('src/tools/QuizManger')).default;
						const quizManager = new QuizManager();

						let quizId = module.quiz.id;

						// Quiz добавили в уже существующий модуль - его ещё нет на сервере
						if (isDraftQuiz) {
							console.log('📝 Creating draft quiz for existing module:', module.id, module.quiz);

							quizId = await quizManager.createQuiz({
								name: module.quiz.name,
								timeEstimate: module.quiz.timeEstimate,
								quizableType: 'marathon_modules',
								quizableId: module.id,
								isActive: module.quiz.isActive ?? true
							});

							console.log('✅ Draft quiz created with id:', quizId);
						}

						// У нового quiz на сервер уходят все вопросы, у существующего - только новые
						const questionsToCreate = isDraftQuiz ? module.quiz.questions : draftQuestions;

						for (const q of questionsToCreate) {
							const { options: questionOptions, id, isDraft, tempId, ...questionData } = q;

							await quizManager.createQuestion(
								quizId,
								questionData,
								questionOptions || []
							);
						}
						console.log('✅ Draft questions created for quiz:', quizId);
					}
				}
			}
			
			if (module.quiz && findModule && findModule.quiz && findModule.quiz.questions && module.quiz.questions
				&& String(findModule.quiz.id) === String(module.quiz.id)) {
				const { updateQuestion } = await import('src/api/quiz');
				
				// Проверяем каждый вопрос на изменение orderNumber
				for (const question of module.quiz.questions) {
					// Пропускаем draft вопросы (у них еще нет ID на сервере)
					if (question.isDraft) continue;
					
					const originalQuestion = findModule.quiz.questions.find(q => q.id === question.id);
					
					if (originalQuestion && originalQuestion.orderNumber !== question.orderNumber) {
						console.log('🔄 Question order changed:', question.id, 'from', originalQuestion.orderNumber, 'to', question.orderNumber);
						
						// Обновляем orderNumber на сервере
						try {
							await updateQuestion({
								quizId: module.quiz.id,
								questionId: question.id,
								payload: {
									...question,
									orderNumber: question.orderNumber
								}
							});
							console.log('✅ Updated question order:', question.id, 'to', question.orderNumber);
						} catch (error) {
							console.error('Error updating question order:', error);
						}
					}
				}
			}

			// Проверяем удалённые вопросы (которые были, но их нет в локальном состоянии)
			// Сравниваем только если на сервере и локально речь об одном и том же quiz
			if (module.quiz && findModule && findModule.quiz && findModule.quiz.questions
				&& String(findModule.quiz.id) === String(module.quiz.id)) {
				const currentQuestionIds = (module.quiz?.questions || []).map(q => q.id);
				const removedQuestions = findModule.quiz.questions.filter(
					oldQuestion => !currentQuestionIds.includes(oldQuestion.id)
				);

				if (removedQuestions.length > 0) {
					console.log('🗑️ Found removed questions:', removedQuestions.map(q => q.id));
					
					const QuizManager = (await import('src/tools/QuizManger')).default;
					
					for (const removedQuestion of removedQuestions) {
						console.log('🗑️ Removing question:', removedQuestion.id, 'from quiz:', module.quiz.id);
						try {
							await QuizManager.removeQuestion(module.quiz.id, removedQuestion.id);
						} catch (error) {
							console.error('Error removing question:', error);
						}
					}
				}
			}

			for (const lecture of module.lectures) {
				
				if (lecture.isDraft) {
					const uploader = new Uploader(lecture.file);
					const contentCreater = new ContentCreater('lecture');
					const stream = await uploader.upload();
					const contentId = await contentCreater.createContent({
						name: lecture.name,
						description: lecture.description,
						streamID: stream.ID,
					});
					await sendLectureToModule({ ...module, id: moduleId }, contentId);
				} else {
					// Тут проверка на соответсвие с исходником
					const findLecture = findModule.lectures.find( item => item.id === lecture.id);
					if (JSON.stringify(lecture) !== JSON.stringify(findLecture)) {
						await dispatch(updateLecture(lecture));
					}
				}
			}
			// Добавляем к модулю лекции, если ранее их не было
			// Удаляем attachments которые были удалены
			if (findModule && findModule.attachments) {
				const currentAttachmentIds = module.attachments.map(a => a.id);
				const removedAttachments = findModule.attachments.filter(
					oldAttachment => !currentAttachmentIds.includes(oldAttachment.id)
				);
				for (const removedAttachment of removedAttachments) {
					console.log('🗑️ Removing attachment:', removedAttachment.id, 'from module:', moduleId);
					try {
						await modulesAPI.removeAttachment({
							marathonId: marathon.id,
							moduleId: moduleId,
							attachmentId: removedAttachment.id
						});
					} catch (error) {
						console.error('Error removing attachment:', error);
					}
				}
			}
		
			// Загружаем новые attachments
			for (const attachment of module.attachments) {
			
				if (attachment.isDraft) {
				
					const uploader = new Uploader(attachment.file);
				
					await uploader.uploadAttachmentOnModule(marathon.id, moduleId, 'marathon');
				
				}
			
			}


			

		}

		queue.push(await updateOrCreateModule());

	}

	await Promise.all(queue);
	// Отправляем только метаданные марафона (без modules — они уже обработаны выше поштучно)
	const { modules, ...marathonMeta } = marathon;
	await dispatch(updateMarathon(marathonMeta, true)); // skipToast = true, т.к. модалка показывается в компоненте

};


export const updateMarathon = (marathon, skipToast = false) => async (dispatch) => {

	try {
		// Преобразуем теги в массив ID перед отправкой
		const marathonPayload = {
			...marathon,
			tags: Array.isArray(marathon.tags) 
				? marathon.tags.map(item => typeof item === 'object' ? item.id : item)
				: []
		};

		const res = await api.updateMarathon( marathon.id, marathonPayload );

		if (res) {
			dispatch(reloadMarathon(marathon.id));
		}

	} catch (e) {

		console.error(e);

		// Показываем тост только если не передан флаг skipToast
		if (!skipToast) {
			dispatch(showErrorToast({
				description: i18n.t('toasts.failedUpdateMarathon')
			}));
		}

		throw e;

	}

};

export const cancelMarathon = (marathonId, publicationId) => async (dispatch) => {
	try {
		const res = await api.cancelMarathon(marathonId);
		if (res) {

			dispatch(reloadPublication(publicationId));
		}
	} catch (e) {
		console.error(e);
		dispatch(
			showErrorToast({
				description: i18n.t('toasts.failedUpdateMarathon'),
			})
		);
		throw e;
	}
};


export const reloadMarathon = (marathonId) => async (dispatch) => {

	try {

		const marathon = await api.marathonById(marathonId);

		if (marathon) {
			dispatch(updateMarathonItem(marathon));
		}

	} catch (error) {

		console.error(error);

		dispatch(showErrorToast({
			description: i18n.t('toasts.failedUpdateCourse')
		}));

		throw error;

	}

};

export const createMarathonModule = ({marathonId, module}) => async (dispatch) => {

	try {

		const moduleId = await api.createMarathonModule({
			marathonId,
			module
		});

		dispatch(reloadMarathon(marathonId));

		return moduleId;

	} catch (error) {

		console.error(error);

		dispatch(showErrorToast({
			description: i18n.t('toasts.failedCreateModule')
		}));

		throw error;

	}

};

export const editMarathonModule = ({ marathonId, module }) => async (dispatch) => {

	try {

		console.log('📤 Sending module update to server:', { marathonId, module });
		await api.editMarathonModule({
			marathonId,
			module
		});

		dispatch(reloadMarathon(marathonId));

	} catch (error) {

		console.error(error);

		dispatch(showErrorToast({
			description: i18n.t('toasts.failedEditModule')
		}));

		throw error;

	}

};

export const removeMarathonModule = ({ marathonId, moduleId }) => async (dispatch) => {

	try {

		await api.removeMarathonModule({
			marathonId,
			moduleId
		});

		dispatch(reloadMarathon(marathonId));

	} catch (error) {

		console.error(error);

		dispatch(showErrorToast({
			description: i18n.t('toasts.failedCreateModule')
		}));

		throw error;

	}

};

export const removeModuleAttachment = ({ marathonId, moduleId, attachmentId }) => async (dispatch, getState ) => {

	try {

		await api.removeAttachmentModule({
			marathonId,
			moduleId,
			attachmentId
		});

		dispatch(reloadMarathon(marathonId));
		

	} catch (error) {

		console.error(error);

		dispatch(showErrorToast({
			description: i18n.t('toasts.failedDeleteModuleFile')
		}));

		throw error;

	}

};

export default marathonsSlice.reducer;
